const HowItWorks = () => {
  return (
    <section id="how-it-works" className="py-20 bg-gradient-to-b from-white to-pink-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            How It Works
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            ReceptionGenie.ai answers your calls 24/7, qualifies leads, and books appointments automatically. Here's how simple it is:
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Step 1 */}
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
            <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center mb-6">
              <span className="text-white text-2xl font-bold">1</span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Call Comes In
            </h3>
            <p className="text-gray-600 leading-relaxed">
              A customer calls your business number — day or night, weekday or weekend. ReceptionGenie answers instantly, every single time.
            </p>
          </div>

          {/* Step 2 */}
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
            <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center mb-6">
              <span className="text-white text-2xl font-bold">2</span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              AI Qualifies the Lead
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Our AI has a natural conversation, asks the right questions, and determines if it's a real opportunity or spam — so you only get quality leads.
            </p>
          </div>

          {/* Step 3 */}
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
            <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center mb-6">
              <span className="text-white text-2xl font-bold">3</span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Appointment Booked
            </h3>
            <p className="text-gray-600 leading-relaxed">
              The AI books the appointment directly into your calendar, sends confirmations, and notifies you — all without you lifting a finger.
            </p>
          </div>
        </div>

        {/* Additional Benefits */}
        <div className="mt-16 bg-white rounded-3xl p-8 lg:p-12 shadow-xl max-w-5xl mx-auto">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Why Businesses Love ReceptionGenie
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-time-line text-pink-600 text-xl"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">24/7 Availability</h4>
                <p className="text-gray-600">Never miss a call again — even after hours, on weekends, or during holidays.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-speed-line text-purple-600 text-xl"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Instant Response</h4>
                <p className="text-gray-600">Answer every call in seconds — speed wins customers before competitors can respond.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-line text-pink-600 text-xl"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Smart Filtering</h4>
                <p className="text-gray-600">AI filters spam and only sends you qualified leads worth your time.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-calendar-check-line text-purple-600 text-xl"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Automatic Booking</h4>
                <p className="text-gray-600">Appointments sync directly to your calendar with instant confirmations sent.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-user-voice-line text-pink-600 text-xl"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Natural Conversations</h4>
                <p className="text-gray-600">Sounds human, not robotic — customers love the experience.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-rocket-line text-purple-600 text-xl"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Quick Setup</h4>
                <p className="text-gray-600">Up and running in minutes — no complex installation or training required.</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <a
            href="#pricing"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-xl transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            Get Started Now
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
